import { FormControl, FormLabel, Stack } from "@mui/material";
import RadioGroupExtended from "../../../common/components/mui-extended/RadioGroupExtended";
import { FieldValues, UseFormReturn, useForm } from "react-hook-form";
import {
  TaxpayerType,
  PersonTaxpayerType,
  EntityTaxpayerType,
  TaxpayerPinLabels,
} from "../../../interfaces/Obligation";
import nameof from "ts-nameof.macro";
import { useEffect } from "react";

type TaxpayerPinTypeModel = {
  taxpayerType: TaxpayerType;
  personTaxpayerType?: PersonTaxpayerType;
  entityTaxpayerType?: EntityTaxpayerType;
};

type TaxpayerPinTypeProps = {
  name: string;
  form: any & UseFormReturn<FieldValues, object>;
};

export default function TaxpayerPinType({ name, form }: TaxpayerPinTypeProps) {
  const defaultModel: TaxpayerPinTypeModel = {
    taxpayerType: TaxpayerType.Person,
    personTaxpayerType: PersonTaxpayerType.Egn,
  };

  const innerForm = useForm<TaxpayerPinTypeModel>({
    mode: "all",
    defaultValues: defaultModel,
  });

  useEffect(() => {
    const parentValue = form.getValues(name);
    if (Object.values(PersonTaxpayerType).includes(parentValue)) {
      innerForm.setValue("taxpayerType", TaxpayerType.Person);
      innerForm.setValue("personTaxpayerType", parentValue);
    }

    if (Object.values(EntityTaxpayerType).includes(parentValue)) {
      innerForm.setValue("taxpayerType", TaxpayerType.Entity);
      innerForm.setValue("entityTaxpayerType", parentValue);
    }
  }, [form.watch(name)]);

  const selectedIdentifierType = innerForm.watch("taxpayerType");
  const selectedPersonIdentifierType = innerForm.watch("personTaxpayerType");
  const selectedEntityIdentifierType = innerForm.watch("entityTaxpayerType");

  useEffect(() => {
    if (selectedIdentifierType === TaxpayerType.Person) {
      form.setValue(name, selectedPersonIdentifierType);
    } else {
      form.setValue(name, selectedEntityIdentifierType);
    }
  }, [selectedIdentifierType, selectedPersonIdentifierType, selectedEntityIdentifierType]);

  return (
    <Stack spacing={1}>
      <RadioGroupExtended
        row
        name={`${nameof(defaultModel.taxpayerType)}`}
        options={[
          [TaxpayerPinLabels.Person, TaxpayerType.Person],
          [TaxpayerPinLabels.Entity, TaxpayerType.Entity],
        ]}
        form={innerForm}
      />
      <FormControl>
        <FormLabel focused={false}>
          {innerForm.watch("taxpayerType") === TaxpayerType.Person
            ? TaxpayerPinLabels.Person
            : TaxpayerPinLabels.Entity}
        </FormLabel>
        {innerForm.watch("taxpayerType") === TaxpayerType.Person && (
          <RadioGroupExtended
            row
            name={`${nameof(defaultModel.personTaxpayerType)}`}
            options={[
              [TaxpayerPinLabels.Egn, PersonTaxpayerType.Egn],
              [TaxpayerPinLabels.Lnch, PersonTaxpayerType.Lnch],
              [TaxpayerPinLabels.NapNumber, PersonTaxpayerType.NapNumber],
            ]}
            form={innerForm}
          />
        )}

        {innerForm.watch("taxpayerType") === TaxpayerType.Entity && (
          <RadioGroupExtended
            row
            name={`${nameof(defaultModel.entityTaxpayerType)}`}
            options={[
              [TaxpayerPinLabels.Bulstat, EntityTaxpayerType.Bulstat],
              [TaxpayerPinLabels.Eik, EntityTaxpayerType.Eik],
            ]}
            form={innerForm}
          />
        )}
      </FormControl>
    </Stack>
  );
}
